<template>
  <div class="bio-design">
    <div class="row">
      <div class="col-12">
        <h3>{{ $t('BIO.LABEL.TEMPLATES') }}</h3>
      </div>
    </div>
    <div class="row">
      <div
        v-for="template in this.bioTemplates"
        :key="template.id"
        class="col-12 col-md-6 col-lg-4"
      >
        <div>
          <div
            class="bio-template"
            :class="{
              'bio-template--active': isActiveTemplate(template.id),
            }"
            @click="selectTemplate(template)"
          >
            <HMFixedAspectRatioContainer aspectRatio="3by4">
              <div class="bio-image-wrapper">
                <img
                  v-if="template.image"
                  :src="template.image"
                  class="bio-image"
                />
                <div
                  v-else
                  class="no-image d-flex justify-content-center align-items-center h-100"
                >
                  <i class="fas fa-image"></i>
                </div>
              </div>
            </HMFixedAspectRatioContainer>
          </div>
          <div class="bio-description">
            {{ template.name }}
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12 px-2">
        <hr />
      </div>
    </div>
    <div class="row align-items-stretch">
      <div class="col-12">
        <h3 class="my-3">{{ $t('BIO.LABEL.STYLING') }}</h3>
      </div>
      <div class="col-lg-6 p-3 background-image-upload">
        <HMImageUpload
          inputName="logo"
          :title="$t('SETTINGS.LABEL.UPLOAD_BACKGROUND_IMAGE')"
          :description="$t('SETTINGS.TEXT.BACKGROUND_IMAGE_MUST_BE')"
          :imageDescription="$t('SETTINGS.LABEL.BACKGROUND_IMAGE')"
          v-model="teacherBio.image"
          canvaDesignType="Logo"
          :canvaWidth="1920"
          :canvaHeight="1080"
          photoType="contain"
          @input="updateBio"
          aspectRatio="none"
        />
      </div>
      <div class="col-lg-6 p-3 profile-image-upload">
        <HMImageUpload
          inputName="profile-photo"
          :title="$t('SETTINGS.LABEL.UPLOAD_PROFILE_PHOTO')"
          :description="$t('SETTINGS.TEXT.UPLOAD_PROFILE_PHOTO_INSTRUCTIONS')"
          :imageDescription="$t('SETTINGS.LABEL.PROFILE_PHOTO')"
          v-model="teacherBio.profile_photo"
          photoType="profile"
          canvaDesignType="Logo"
          :canvaWidth="400"
          :canvaHeight="400"
          @input="updateBio"
          :round="true"
          aspectRatio="none"
        />
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6 px-3 py-2">
        <div class="d-flex flex-row justify-content-start align-items-center">
          <span class="color-label">
            {{ $t('SETTINGS.TITLE.PRIMARY_ACCENT_COLOR') }}:
          </span>
          <v-swatches
            v-model="teacherBio.font_color"
            show-fallback
            :fallback-ok-text="$t('GENERAL.BUTTON.CONFIRM')"
            fallback-ok-class="color-picker__confirm-button"
            :swatch-style="{
              borderRadius: '50%',
              width: '24px',
              height: '24px',
            }"
            @close="updateBio"
          >
            <div slot="trigger" class="mt-3">
              <button
                :style="`background-color: ${teacherBio.font_color};`"
                class="color-picker__circle"
              ></button>
            </div>
          </v-swatches>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6 px-3 py-2">
        <div class="d-flex flex-row justify-content-start align-items-center">
          <span class="color-label">
            {{ $t('SETTINGS.TITLE.SOCIAL_ICONS') }}:
          </span>
          <v-swatches
            v-model="teacherBio.icons_color"
            show-fallback
            :fallback-ok-text="$t('GENERAL.BUTTON.CONFIRM')"
            fallback-ok-class="color-picker__confirm-button"
            :swatch-style="{
              borderRadius: '50%',
              width: '24px',
              height: '24px',
            }"
            @close="updateBio"
          >
            <div slot="trigger" class="mt-3">
              <button
                :style="`background-color: ${teacherBio.icons_color};`"
                class="color-picker__circle"
              ></button>
            </div>
          </v-swatches>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-6 px-3 py-2">
        <div class="d-flex flex-row justify-content-start align-items-end">
          <span class="c-charcoal"> {{ $t('SETTINGS.TITLE.FONT') }}: </span>
          <div class="d-flex flex-wrap">
            <div class="font-selection px-3 w-100">
              <link
                :href="`https://fonts.googleapis.com/css2?family=${teacher.default_font}:wght@400;500;700&display=swap`"
                rel="stylesheet"
              />
              <div class="mt-2" :style="`font-family: '${defaultFontLabel}'`">
                <button
                  class="h2 underline bg-white border-0 mb-0"
                  @click="openSelectFontModal"
                >
                  {{ defaultFontLabel }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { errorToMessage } from '@/core/utils/errors';
import { mapGetters } from 'vuex';
import { formatObjectForBackend } from '@/core/api/utils/converter';
import { cloneDeep } from 'lodash';
import VSwatches from 'vue-swatches';
import BrandingStudioFontModal from '@/web/components/settings/BrandingStudioFontModal.vue';

export default {
  name: 'BioDesign',
  components: {
    VSwatches,
  },
  computed: {
    ...mapGetters({
      bioTemplates: 'setting/bioTemplates',
      teacherBio: 'setting/marvelousBio',
      teacher: 'user/teacher',
    }),
    defaultFontLabel() {
      if (!this.teacher?.default_font) {
        return;
      }
      const selectedFont = this.teacher.available_fonts.find(
        font => font.value === this.teacher.default_font
      );
      return selectedFont.label;
    },
  },
  methods: {
    async loadData() {
      try {
        await Promise.all([
          this.$store.dispatch('setting/getMarvelousBioInfo'),
          this.$store.dispatch('setting/getBioTemplates'),
        ]);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('CUSTOMERS.NOTIFICATIONS.CUSTOMER_ENROLL_ERROR'),
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async updateBio() {
      try {
        const formattedBio = formatObjectForBackend(cloneDeep(this.teacherBio));
        await this.$store.dispatch(
          'setting/updateMarvelousBioInfo',
          formattedBio
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          text: errorToMessage(error.response),
        });
      }
    },
    selectTemplate(template) {
      this.teacherBio.template = template.id;
      this.updateBio();
    },
    openSelectFontModal() {
      this.$modal.show(
        BrandingStudioFontModal,
        {
          fonts: this.teacher.available_fonts,
          selectedFont: this.teacher.default_font,
        },
        null,
        { 'before-close': this.setSelectedFont }
      );
    },
    setSelectedFont($event) {
      if (!$event.params) {
        return;
      }
      this.updateTeacherInfo({ teacher: { default_font: $event.params } });
    },
    async updateTeacherInfo(data) {
      this.inProgress = true;
      try {
        await this.$store.dispatch('user/updateTeacher', data);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Update failed',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
      this.inProgress = false;
    },
    isActiveTemplate(id) {
      return this.teacherBio?.template === id;
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.bio-template {
  cursor: pointer;
  max-width: 270px;
  margin: 0.25rem;
  padding: 5px;

  border: 2px solid transparent;

  &--active {
    border: 2px solid $coral;
    border-radius: 10px;
  }
}

.bio-image-wrapper {
  border-radius: 6px;
  box-shadow: 6px 6px 28px -6px $light-gray;
  overflow: hidden;

  .bio-image {
    object-fit: cover;
    max-width: 100%;
  }

  .no-image {
    font-size: 36px;
  }
}

.bio-description {
  padding-left: 0.7rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: $charcoal;
}

.vue-swatches {
  display: block;
  ::v-deep & > div {
    display: block;
  }
  ::v-deep .vue-swatches__fallback__wrapper {
    display: flex;
  }
  ::v-deep .vue-swatches__fallback__input {
    box-sizing: content-box;
  }
  ::v-deep .color-picker__confirm-button {
    background-color: var(--brand-color, $coral);
  }
}
.color-picker__container {
  width: 100%;
  border-radius: $border-radius;
}
.color-picker__circle {
  width: 45px;
  height: 45px;
  border-radius: 100%;
  outline: none;
  border: 1px solid $light-gray;
}
.color-label {
  color: $charcoal;
  line-height: 3;
  width: 140px;
}
.underline {
  text-decoration: underline;
}

.background-image-upload {
  max-width: 400px;
  max-height: 300px;
  width: 400px;
  height: 300px;
}

.profile-image-upload {
  max-width: 300px;
  max-height: 300px;
  width: 300px;
  height: 300px;
}
</style>
